/** 内容中心左侧列表的一条内容，只依赖分组需要的稳定字段。 */
export interface PackageScopedEntry {
  packageId: string;
  packageCode: string;
  contentType: 'VIDEO' | 'ARTICLE';
}

export interface ContentPackageGroup<T extends PackageScopedEntry> {
  packageId: string;
  packageCode: string;
  contentType: 'VIDEO' | 'ARTICLE';
  entries: T[];
}

/**
 * 同一项目下的不同服务包各自从 1 开始编号（老编号按医生批次回填），
 * 直接平铺会看起来像“任务重叠”（BUG-0078）；按服务包分组后列表才能区分来源。
 */
export function groupContentByPackage<T extends PackageScopedEntry>(entries: readonly T[]): ContentPackageGroup<T>[] {
  const groups = new Map<string, ContentPackageGroup<T>>();
  for (const entry of entries) {
    const key = `${entry.contentType}:${entry.packageId}`;
    let group = groups.get(key);
    if (!group) {
      group = { packageId: entry.packageId, packageCode: entry.packageCode,
        contentType: entry.contentType, entries: [] };
      groups.set(key, group);
    }
    group.entries.push(entry);
  }
  return [...groups.values()];
}

/** 服务包切换器使用的可读名称：优先历史包标题，其次 内容类型 + 模板名 + 服务周期，避免只显示看不懂的包编号。 */
export interface PackageLabelInput {
  contentType: 'VIDEO' | 'ARTICLE';
  /** 历史迁移包在标准快照里保存的可读标题（如「沈雯-2026-视频」），只有历史包才有。 */
  packageTitle?: string | null;
  templateName?: string | null;
  periodFrom?: string | null;
  periodUntil?: string | null;
  packageCode?: string | null;
}

export function packageDisplayLabel(input: PackageLabelInput): string {
  const type = input.contentType === 'VIDEO' ? '视频' : '文章';
  const legacyTitle = input.packageTitle?.trim();
  if (legacyTitle) return legacyTitle;
  const template = input.templateName?.trim();
  const period = input.periodFrom && input.periodUntil
    ? `${input.periodFrom} ~ ${input.periodUntil}` : '';
  if (template && period) return `${type} · ${template} · ${period}`;
  if (template) return `${type} · ${template}`;
  if (period) return `${type}服务包 · ${period}`;
  return `${type}服务包${input.packageCode ? ` · ${input.packageCode}` : ''}`;
}

/**
 * BUG-0078：默认只显示一个服务包（列表中的第一个），保留显式请求的包；
 * 只有用户主动选“全部服务包”时才返回 ALL。
 */
export function resolvePackageFilter(entries: readonly PackageScopedEntry[],
  requested: string | null | undefined): string {
  if (requested === 'ALL') return 'ALL';
  if (requested && entries.some((entry) => entry.packageId === requested)) return requested;
  return entries[0]?.packageId ?? 'ALL';
}



/**
 * BUG-0185：服务包状态在两种投影里位置不同——最小包投影（策划/执行人，无「服务包」页权限）
 * 把状态放在 `item.status`，完整服务包摘要在 `item.summary.status`。判定必须统一，
 * 否则只认 summary 的判定对最小投影恒为空，页面会漏掉创建内容入口。
 */
export interface PackageStatusProjection {
  status?: string | null | undefined;
  summary?: { status?: string | null | undefined } | undefined;
}

/** 统一读取服务包状态：最小投影优先 `status`，完整摘要回退 `summary.status`。 */
export function descriptorStatus(item: PackageStatusProjection): string | undefined {
  return item.status ?? item.summary?.status ?? undefined;
}

/** 可读取内容的服务包：ACTIVE/COMPLETED；完全拿不到状态的老投影保持可读，避免误伤。 */
export function isReadableDescriptor(item: PackageStatusProjection): boolean {
  const status = descriptorStatus(item);
  return status === undefined || status === 'ACTIVE' || status === 'COMPLETED';
}

/** 可以在其下创建内容的活动服务包。 */
export function isActiveDescriptor(item: PackageStatusProjection): boolean {
  return descriptorStatus(item) === 'ACTIVE';
}
